/**
 * Admin section showing per-category labor quotas (computed from the accepted
 * quote + amendments), with override-with-history support, and the list of
 * "articles completed" by workers via the post-pointage flow.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Hammer, PencilSimple, ClockCounterClockwise, X, CheckCircle, Package, FileText } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtH = (n) => `${(Math.round((Number(n) || 0) * 10) / 10).toLocaleString('fr-FR')} h`;
const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString('fr-FR'); } catch { return iso || '—'; } };

/**
 * One row of the per-article advancement table. Lets the admin override the
 * cumulative quantity_done via PUT /article-completions/manual. The override
 * is stored as a "manual" completion document so worker-derived progress
 * stays auditable.
 */
const ArticleProgressRow = ({ a, projectId, onReload }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(a.quantity_done || 0));
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { setValue(String(a.quantity_done || 0)); }, [a.quantity_done]);

  const pct = a.quantity_total > 0 ? Math.min(100, (a.quantity_done / a.quantity_total) * 100) : 0;
  const color = pct >= 100 ? '#047857' : pct >= 50 ? '#1565C0' : '#EF6C00';

  const save = async () => {
    const n = parseFloat(String(value).replace(',', '.'));
    if (Number.isNaN(n) || n < 0) { toast.error('Quantité invalide'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/projects/${projectId}/article-completions/manual`, {
        library_item_id: a.library_item_id,
        quantity_done: n,
      }, { withCredentials: true });
      toast.success('Avancement mis a jour');
      setEditing(false);
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid={`article-progress-${a.library_item_id}`}
      style={{ padding: '6px 10px', border: '1px solid #E2E2E6', borderRadius: 6, background: 'white' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, fontSize: '0.82rem' }}>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
          <span style={{ color: '#5E5F66', fontSize: '0.72rem', marginRight: 6 }}>{a.category}</span>
          {a.description}
        </span>
        <span style={{ fontWeight: 600, whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 4 }}>
          {editing ? (
            <>
              <input
                type="text"
                inputMode="decimal"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                data-testid={`article-done-input-${a.library_item_id}`}
                style={{ width: 70, textAlign: 'right', padding: '2px 6px', border: '1px solid #1565C0', borderRadius: 4, fontSize: '0.85rem' }}
                autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setValue(String(a.quantity_done || 0)); setEditing(false); } }}
              />
              <span style={{ color: '#5E5F66', fontSize: '0.72rem' }}>/ {a.quantity_total.toLocaleString('fr-FR')} {a.unit || ''}</span>
              <button type="button" onClick={save} disabled={saving} data-testid={`article-done-save-${a.library_item_id}`}
                style={{ background: '#047857', color: 'white', border: 'none', borderRadius: 4, padding: '2px 8px', cursor: 'pointer', fontSize: '0.72rem' }}>
                {saving ? '...' : 'OK'}
              </button>
              <button type="button" onClick={() => { setValue(String(a.quantity_done || 0)); setEditing(false); }}
                style={{ background: 'transparent', border: 'none', color: '#9E9EA7', cursor: 'pointer', fontSize: '0.9rem' }}>×</button>
            </>
          ) : (
            <>
              <button type="button" onClick={() => setEditing(true)} data-testid={`article-done-edit-${a.library_item_id}`}
                title="Modifier l'avancement"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, color: '#1F2024', fontWeight: 600 }}>
                {a.quantity_done.toLocaleString('fr-FR')} / {a.quantity_total.toLocaleString('fr-FR')} {a.unit || ''}
                <PencilSimple size={11} style={{ marginLeft: 4, verticalAlign: 'middle', opacity: 0.5 }} />
              </button>
              <span style={{ color, marginLeft: 2 }}>({Math.round(pct)}%)</span>
            </>
          )}
        </span>
      </div>
      <div style={{ height: 4, background: '#F4F5F7', borderRadius: 999, marginTop: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: pct + '%', background: color, transition: 'width 0.3s' }} />
      </div>
    </div>
  );
};

const QuotaRow = ({ row, projectId, onReload }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(row.hours_current));
  const [reason, setReason] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const save = async () => {
    try {
      await axios.put(`${API_URL}/api/projects/${projectId}/labor-quotas`, {
        source: row.source, category: row.category,
        amendment_id: row.amendment_id || null,
        hours_current: parseFloat(String(value).replace(',', '.')) || 0,
        reason,
      }, { withCredentials: true });
      toast.success('Quota mis à jour');
      setEditing(false); setReason('');
      onReload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <>
      <tr data-testid={`quota-row-${row.source}-${row.category}${row.amendment_id ? '-' + row.amendment_id : ''}`}>
        <td>
          {row.source === 'initial' ? (
            <span>Devis initial</span>
          ) : (
            <span style={{ color: '#7B1FA2' }}>Avenant {row.amendment_label}</span>
          )}
        </td>
        <td style={{ fontWeight: 600 }}>{row.category}</td>
        <td style={{ textAlign: 'right', color: '#5E5F66' }}>{fmtH(row.hours_initial)}</td>
        <td style={{ textAlign: 'right', fontWeight: 700 }}>
          {editing ? (
            <input type="text" inputMode="decimal" value={value}
              onChange={(e) => setValue(e.target.value)}
              data-testid={`quota-input-${row.category}`}
              style={{ width: 80, textAlign: 'right', padding: '2px 6px', border: '1px solid #D1D5DB', borderRadius: 4 }} />
          ) : (
            <span style={{ color: row.is_overridden ? '#EF6C00' : '#1F2024' }}>
              {fmtH(row.hours_current)}{row.is_overridden && <span style={{ fontSize: '0.65rem', marginLeft: 4 }} title="Modifié manuellement">✎</span>}
            </span>
          )}
        </td>
        <td style={{ textAlign: 'right', width: 110 }}>
          {editing ? (
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              <input type="text" placeholder="Motif (optionnel)" value={reason}
                onChange={(e) => setReason(e.target.value)}
                style={{ width: 130, padding: '2px 6px', border: '1px solid #D1D5DB', borderRadius: 4, fontSize: '0.78rem' }}
                data-testid="quota-reason-input" />
              <button className="btn btn-primary btn-sm" onClick={save} data-testid="quota-save-btn"><CheckCircle size={12} /></button>
              <button className="btn btn-secondary btn-sm" onClick={() => { setEditing(false); setValue(String(row.hours_current)); }} data-testid="quota-cancel-btn"><X size={12} /></button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              {(row.history?.length || 0) > 0 && (
                <button className="btn btn-secondary btn-sm" onClick={() => setShowHistory((s) => !s)} title="Historique" data-testid={`quota-history-btn-${row.category}`}>
                  <ClockCounterClockwise size={12} />
                </button>
              )}
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(true)} data-testid={`quota-edit-btn-${row.category}`}>
                <PencilSimple size={12} />
              </button>
            </div>
          )}
        </td>
      </tr>
      {showHistory && (row.history || []).map((h, i) => (
        <tr key={i} style={{ background: '#FAFAFA', fontSize: '0.78rem', color: '#5E5F66' }}>
          <td colSpan={5} style={{ padding: '4px 14px' }}>
            <span>{fmtDate(h.at)} · {h.by} · {fmtH(h.from)} → {fmtH(h.to)}{h.reason ? ` · ${h.reason}` : ''}</span>
          </td>
        </tr>
      ))}
    </>
  );
};

const LaborQuotasSection = ({ project }) => {
  const projectId = project?.id;
  const [data, setData] = useState(null);
  const [completions, setCompletions] = useState([]);
  const [articles, setArticles] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showProposal, setShowProposal] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const [q, c, a, p] = await Promise.all([
        axios.get(`${API_URL}/api/projects/${projectId}/labor-quotas`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects/${projectId}/article-completions`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects/${projectId}/quote-articles`, { withCredentials: true }),
        axios.get(`${API_URL}/api/projects/${projectId}/invoice-proposal`, { withCredentials: true }).catch(() => ({ data: null })),
      ]);
      setData(q.data);
      setCompletions(c.data || []);
      setArticles(a.data || []);
      setProposal(p.data);
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return null;
  if (!data) return null;

  const remaining = data.total_remaining;
  const pct = data.total_current > 0 ? Math.min(100, Math.round((data.total_pointed / data.total_current) * 100)) : 0;

  return (
    <div className="card" style={{ padding: '14px 18px', marginBottom: 18 }} data-testid="labor-quotas-section">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: '0.95rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Hammer size={16} weight="bold" /> Quota d'heures MO par catégorie (devis + avenants)
        </h3>
        <div style={{ display: 'flex', gap: 18, fontSize: '0.78rem' }}>
          <div>Prévu : <strong>{fmtH(data.total_current)}</strong></div>
          <div>Pointé : <strong>{fmtH(data.total_pointed)}</strong></div>
          <div>Restant : <strong style={{ color: remaining < 0 ? '#C62828' : '#047857' }}>{fmtH(remaining)}</strong></div>
        </div>
      </div>
      {data.total_current > 0 && (
        <div style={{ height: 8, background: '#F4F5F7', borderRadius: 999, overflow: 'hidden', marginBottom: 10 }}>
          <div style={{ height: '100%', width: pct + '%', background: pct < 80 ? '#047857' : pct < 100 ? '#EF6C00' : '#C62828', transition: 'width 0.3s' }} />
        </div>
      )}
      {data.rows.length === 0 ? (
        <div style={{ padding: 12, textAlign: 'center', color: '#5E5F66', fontSize: '0.85rem' }}>
          Aucun devis accepté pour ce chantier — le quota MO se calculera automatiquement dès qu'un devis sera validé.
        </div>
      ) : (
        <table className="data-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Source</th>
              <th>Catégorie</th>
              <th style={{ textAlign: 'right', width: 110 }}>Initial (devis)</th>
              <th style={{ textAlign: 'right', width: 110 }}>Courant</th>
              <th style={{ width: 110 }}></th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <QuotaRow key={i} row={row} projectId={projectId} onReload={fetchAll} />
            ))}
          </tbody>
        </table>
      )}

      {/* Soft alert: significant amount ready to bill + no recent invoice */}
      {(() => {
        if (!proposal || !proposal.total_proposed_ht) return null;
        const market = Number(project?.sale_price_ht || 0);
        const readyPct = market > 0 ? (proposal.total_proposed_ht / market) * 100 : 0;
        let daysSince = 9999;
        if (proposal.last_invoice_date) {
          try {
            const diffMs = Date.now() - new Date(proposal.last_invoice_date).getTime();
            daysSince = Math.floor(diffMs / 86400000);
          } catch { /* noop */ }
        }
        const shouldAlert = readyPct >= 30 && daysSince >= 30;
        if (!shouldAlert) return null;
        return (
          <div data-testid="billing-due-alert"
            style={{
              marginTop: 14, padding: '10px 14px',
              background: '#FEF3C7', border: '1px solid #FDE68A', borderRadius: 8,
              display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            }}>
            <span style={{ fontSize: '1.2rem' }}>💰</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ color: '#92400E' }}>
                {proposal.total_proposed_ht.toLocaleString('fr-FR')} EUR prêts à facturer
              </strong>
              <div style={{ fontSize: '0.78rem', color: '#92400E', marginTop: 2 }}>
                Soit {Math.round(readyPct)}% du marché.
                {proposal.last_invoice_date
                  ? ` Dernière facture il y a ${daysSince} jours.`
                  : ' Aucune facture émise pour le moment.'}
              </div>
            </div>
            <button type="button" className="btn btn-sm" onClick={() => setShowProposal(true)}
              data-testid="billing-due-alert-cta"
              style={{ background: '#92400E', color: 'white', padding: '4px 10px', borderRadius: 4, fontSize: '0.78rem', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              Voir la proposition
            </button>
          </div>
        );
      })()}

      {/* Per-article progress bars */}
      {articles.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <h4 style={{ margin: 0, fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Package size={14} /> Avancement physique par article
            </h4>
            <button type="button" className="btn btn-primary btn-sm"
              onClick={() => setShowProposal(true)}
              data-testid="invoice-proposal-open-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <FileText size={14} /> Proposition de facturation
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {articles.map((a) => (
              <ArticleProgressRow key={a.library_item_id} a={a} projectId={projectId} onReload={fetchAll} />
            ))}
          </div>
        </div>
      )}

      {/* Articles completed */}
      <h4 style={{ margin: '18px 0 8px', fontSize: '0.88rem', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Package size={14} /> Articles réalisés sur le chantier ({completions.length})
      </h4>
      {completions.length === 0 ? (
        <div style={{ padding: 8, fontSize: '0.78rem', color: '#5E5F66' }}>
          Les salariés peuvent saisir les articles réalisés après chaque pointage.
        </div>
      ) : (
        <div style={{ maxHeight: 260, overflow: 'auto' }}>
          <table className="data-table" style={{ width: '100%', fontSize: '0.82rem' }}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Opérateur</th>
                <th>Catégorie</th>
                <th>Article</th>
                <th style={{ textAlign: 'right' }}>Quantité</th>
              </tr>
            </thead>
            <tbody>
              {completions.map((c) => (
                <tr key={c.id} data-testid={`completion-row-${c.id}`}>
                  <td>{c.date}</td>
                  <td>{c.user_name}</td>
                  <td style={{ color: '#5E5F66' }}>{c.category}</td>
                  <td>{c.description}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{c.quantity}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showProposal && (
        <InvoiceProposalModal projectId={projectId} onClose={() => setShowProposal(false)} />
      )}
    </div>
  );
};

const fmtEUR = (n) => `${(Math.round((Number(n) || 0) * 100) / 100).toLocaleString('fr-FR')} EUR`;

const InvoiceProposalModal = ({ projectId, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/projects/${projectId}/invoice-proposal`, { withCredentials: true })
      .then((r) => setData(r.data))
      .catch(() => setData({ lines: [], total_proposed_ht: 0, total_done_ht: 0, total_already_billed_ht: 0 }))
      .finally(() => setLoading(false));
  }, [projectId]);

  const copyToClipboard = () => {
    if (!data?.lines?.length) return;
    const txt = data.lines.filter((l) => l.quantity_proposed > 0)
      .map((l) => `${l.description}\t${l.quantity_proposed} ${l.unit}\t${l.unit_price_ht} EUR\t${l.line_proposed_ht} EUR`)
      .join('\n');
    navigator.clipboard.writeText(txt).then(() => toast.success('Lignes copiées'));
  };

  return (
    <div role="dialog" aria-modal="true"
      data-testid="invoice-proposal-modal"
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 12 }}>
      <div style={{ background: 'white', borderRadius: 12, width: '100%', maxWidth: 1000, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Proposition de facturation sur avancement</h3>
            <div style={{ fontSize: '0.78rem', color: '#5E5F66', marginTop: 2 }}>
              Basée sur les articles réalisés et déduction de ce qui a déjà été facturé.
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="invoice-proposal-close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 6 }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: 18 }}>
          {loading && <div style={{ textAlign: 'center', color: '#5E5F66' }}>Calcul en cours…</div>}
          {!loading && data && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 14 }}>
                <div style={{ padding: '10px 14px', background: '#F4F5F7', borderRadius: 8 }}>
                  <div style={{ fontSize: '0.72rem', color: '#5E5F66', textTransform: 'uppercase' }}>Réalisé HT</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>{fmtEUR(data.total_done_ht)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: '#FEF3C7', borderRadius: 8 }}>
                  <div style={{ fontSize: '0.72rem', color: '#92400E', textTransform: 'uppercase' }}>Déjà facturé HT</div>
                  <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#92400E' }}>{fmtEUR(data.total_already_billed_ht)}</div>
                </div>
                <div style={{ padding: '10px 14px', background: '#D1FAE5', borderRadius: 8 }}>
                  <div style={{ fontSize: '0.72rem', color: '#047857', textTransform: 'uppercase' }}>À facturer HT</div>
                  <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#047857' }} data-testid="proposal-total-ht">{fmtEUR(data.total_proposed_ht)}</div>
                </div>
              </div>
              {data.lines.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: '#5E5F66' }}>Pas de devis accepté ou aucune avancée enregistrée.</div>
              ) : (
                <table className="data-table" style={{ width: '100%', fontSize: '0.82rem' }}>
                  <thead>
                    <tr>
                      <th>Catégorie</th>
                      <th>Article</th>
                      <th style={{ textAlign: 'right' }}>PU HT</th>
                      <th style={{ textAlign: 'right' }}>Réalisé</th>
                      <th style={{ textAlign: 'right' }}>Déjà facturé</th>
                      <th style={{ textAlign: 'right' }}>À facturer</th>
                      <th style={{ textAlign: 'right' }}>Montant HT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.lines.map((l) => (
                      <tr key={l.library_item_id} data-testid={`proposal-row-${l.library_item_id}`}
                        style={{ background: l.quantity_proposed > 0 ? 'white' : '#FAFAFA', color: l.quantity_proposed > 0 ? '#1F2024' : '#9CA3AF' }}>
                        <td>{l.category}</td>
                        <td>{l.description}</td>
                        <td style={{ textAlign: 'right' }}>{l.unit_price_ht.toLocaleString('fr-FR')} EUR</td>
                        <td style={{ textAlign: 'right' }}>{l.quantity_done} / {l.quantity_total} {l.unit}</td>
                        <td style={{ textAlign: 'right' }}>{l.quantity_billed} {l.unit}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700 }}>{l.quantity_proposed} {l.unit}</td>
                        <td style={{ textAlign: 'right', fontWeight: 700, color: '#047857' }}>{fmtEUR(l.line_proposed_ht)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>
        <div style={{ padding: 14, borderTop: '1px solid #E2E2E6', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" className="btn btn-secondary" onClick={copyToClipboard} data-testid="proposal-copy-btn"
            disabled={!data?.lines?.some((l) => l.quantity_proposed > 0)}>
            Copier les lignes
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
};

export default LaborQuotasSection;
